import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, IndianRupee, Users, Clock, FileText, Download, FlaskConical, FolderOpen, BookOpen, ArrowRight, BarChart2, Target, Upload, Mail, Stethoscope } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { outcomeLabel } from "@/lib/outcomes";

// Stable chart config — module scope so recharts children don't re-render on identity change
const CHART_MARGIN = { top: 8, right: 40, bottom: 8, left: 8 };
const CHART_TICK_XS = { fontSize: 12 };
const CHART_TICK_YSLATE = { fontSize: 12 };
const CHART_CURSOR = { fill: "#F1F5F9" };
const BAR_RADIUS = [0, 4, 4, 0];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [insights, setInsights] = useState(null);
  const [filter, setFilter] = useState("pending");

  const [insightsModule, setInsightsModule] = useState("all");
  const loadAll = useCallback(async (statusFilter = filter) => {
    try {
      const [s, p, u, ins, refs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/payments", { params: statusFilter ? { status_filter: statusFilter } : {} }),
        api.get("/admin/users"),
        api.get("/admin/insights", { params: { module: insightsModule } }),
        api.get("/admin/insights/referrers"),
      ]);
      setStats(s.data); setPayments(p.data); setUsers(u.data);
      setInsights(ins.data); setReferrers(refs.data.referrers);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filter, insightsModule]);
  useEffect(() => { loadAll(filter); }, [filter, loadAll]);

  const approve = async (id) => {
    try { await api.post(`/admin/payments/${id}/approve`); toast.success("Access activated"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/payments/${id}/reject`); toast.success("Payment rejected"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [anon, setAnon] = useState(true);
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/cases/export.csv", { params: { anonymised: anon }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rif_cases_${anon ? "anon" : "labeled"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Cohort CSV downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [importResult, setImportResult] = useState(null);
  const importCsv = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/outcomes/import", form);
      setImportResult(data);
      toast.success(`${data.updated} case${data.updated === 1 ? "" : "s"} updated`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [referrers, setReferrers] = useState(null);

  const [digestBusy, setDigestBusy] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/email/monthly-digest");
      setDigestResult(data);
      toast.success(data.sent ? `Digest emailed to admin (${data.period})` : `Digest computed (${data.period}). Email skipped — set RESEND_API_KEY to send.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDigestBusy(false); }
  };

  return (
    <div className="reveal">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Admin console</div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Owner dashboard</h1>
          <p className="mt-2 text-sm text-slate-600">Manage users, review UPI payments and track revenue.</p>
        </div>
        <div className="flex items-center gap-3 border border-slate-200 rounded-md bg-white p-3">
          <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
            <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="csv-anon-toggle" /> Anonymise labels
          </label>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" strokeWidth={1.75} /> Export cases CSV
          </Button>
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 cursor-pointer" data-testid="import-csv-label">
            <Upload className="h-4 w-4" strokeWidth={1.75} /> Import outcomes CSV
            <input
              type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; importCsv(f); }}
              data-testid="import-csv-input"
            />
          </label>
          <Button size="sm" variant="outline" onClick={sendDigest} disabled={digestBusy} data-testid="send-digest-btn">
            <Mail className="h-4 w-4 mr-2" strokeWidth={1.75} /> {digestBusy ? "Sending…" : "Send monthly digest"}
          </Button>
        </div>
      </div>

      {/* Clinical shortcuts — the admin is also the clinician */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Link to="/workflow" className="group flex items-start gap-4 p-5 rounded-md border-2 border-blue-900 bg-blue-900 text-white hover:bg-blue-800 transition-colors" data-testid="admin-shortcut-workflow">
          <FlaskConical className="h-6 w-6 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg">Try the RIF Workflow</div>
            <div className="text-sm text-blue-100 mt-0.5">Run the 5-step interactive protocol on a sample patient.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">
              Start a case <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.75} />
            </div>
          </div>
        </Link>
        <Link to="/cases" className="group flex items-start gap-4 p-5 rounded-md border border-slate-200 bg-white hover:border-blue-900 transition-colors" data-testid="admin-shortcut-cases">
          <FolderOpen className="h-6 w-6 text-blue-900 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-slate-900">Patient cases</div>
            <div className="text-sm text-slate-600 mt-0.5">Open, re-export or delete your saved cases.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-900">
              Open cases <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.75} />
            </div>
          </div>
        </Link>
        <Link to="/library" className="group flex items-start gap-4 p-5 rounded-md border border-slate-200 bg-white hover:border-blue-900 transition-colors" data-testid="admin-shortcut-library">
          <BookOpen className="h-6 w-6 text-blue-900 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-slate-900">Research library</div>
            <div className="text-sm text-slate-600 mt-0.5">ESHRE ’23, ASRM, ERA, KIR/HLA references.</div>
            <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-900">
              Browse <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.75} />
            </div>
          </div>
        </Link>
      </div>

      {/* Stats */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total users" value={stats?.total_users ?? "—"} sub={`${stats?.active_users ?? "—"} active`} testId="stat-users" />
        <StatCard icon={Clock} label="Pending payments" value={stats?.pending_payments ?? "—"} sub="Awaiting review" testId="stat-pending" />
        <StatCard icon={IndianRupee} label="Revenue" value={stats ? `₹${stats.revenue_inr.toLocaleString("en-IN")}` : "—"} sub={`${stats?.approved_payments ?? "—"} approved`} testId="stat-revenue" />
        <StatCard icon={FileText} label="Cases created" value={stats?.total_cases ?? "—"} sub="All clinicians" testId="stat-cases" />
      </div>

      <Tabs defaultValue="payments" className="mt-10">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Cohort insights</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            {["pending", "approved", "rejected", ""].map((s) => (
              <button key={s || "all"} onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-sm border ${filter === s ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                data-testid={`filter-${s || "all"}`}>
                {s ? s[0].toUpperCase() + s.slice(1) : "All"}
              </button>
            ))}
          </div>
          <div className="border border-slate-200 bg-white rounded-md divide-y divide-slate-200" data-testid="admin-payments-list">
            {payments.length === 0 && <div className="p-6 text-sm text-slate-500">No payments in this view.</div>}
            {payments.map((p) => (
              <div key={p.id} className="p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{p.user_name} · <span className="text-slate-500 font-normal">{p.user_email}</span></div>
                  <div className="mt-1 text-xs text-slate-500">
                    UTR <span className="font-mono text-slate-900">{p.transaction_ref}</span> · ₹{p.amount.toLocaleString("en-IN")} · {new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.payer_note && <div className="mt-1 text-xs text-slate-600 italic">“{p.payer_note}”</div>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={p.status} />
                  {p.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" strokeWidth={1.75} /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}>
                        <XCircle className="h-4 w-4 mr-1 text-red-600" strokeWidth={1.75} /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="border border-slate-200 bg-white rounded-md divide-y divide-slate-200" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.id} className="p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email} · joined {new Date(u.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-500">{u.role}</span>
                  <StatusBadge status={u.access_status === "active" ? "approved" : u.access_status === "rejected" ? "rejected" : "pending"} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="insights" className="mt-4">
          <div className="flex items-center gap-2 mb-4" data-testid="insights-module-tabs">
            {[
              { v: "all", l: "All modules" },
              { v: "rif", l: "RIF only" },
              { v: "rpl", l: "RPL only" },
            ].map((m) => (
              <button
                key={m.v}
                onClick={() => setInsightsModule(m.v)}
                className={`text-xs px-3 py-1.5 rounded-sm border ${insightsModule === m.v ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                data-testid={`insights-module-${m.v}`}>
                {m.l}
              </button>
            ))}
          </div>
          {importResult && (
            <div className="mb-4 border-l-4 border-teal-700 bg-teal-50 p-3 rounded-sm text-sm" data-testid="import-result">
              Imported <b>{importResult.updated}</b> outcome{importResult.updated === 1 ? "" : "s"}
              {importResult.error_count > 0 && (
                <>
                  {" · "}<b>{importResult.error_count}</b> row{importResult.error_count === 1 ? "" : "s"} rejected
                  <details className="mt-2">
                    <summary className="cursor-pointer text-slate-700">Show errors</summary>
                    <ul className="mt-1 ml-4 list-disc text-xs text-slate-600">
                      {importResult.errors.map((e) => (
                        <li key={`${e.line}-${e.case_id}`}>Line {e.line} ({e.case_id}): {e.reason}</li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </div>
          )}
          <InsightsPane data={insights} />
          <ReferrersPane referrers={referrers} />
          {digestResult && (
            <div className="mt-6 border-l-4 border-blue-900 bg-blue-50 p-3 rounded-sm text-sm" data-testid="digest-preview">
              <b>{digestResult.period} digest preview</b> — {digestResult.stats.new_cases} new case{digestResult.stats.new_cases === 1 ? "" : "s"}, {digestResult.stats.new_outcomes} outcomes recorded, ₹{digestResult.stats.revenue_this_month.toLocaleString("en-IN")} revenue.
              {!digestResult.sent && <div className="mt-1 text-xs text-slate-600">Email delivery was skipped — set <code className="font-mono">RESEND_API_KEY</code> in the backend .env to enable.</div>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="border border-slate-200 bg-white p-4 rounded-md" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
      </div>
      <div className="mt-2 font-mono text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    approved: "bg-teal-50 text-teal-800 border-teal-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
  };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${map[status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{status}</span>;
}

function InsightsPane({ data }) {
  if (!data) return <div className="p-6 text-sm text-slate-500">Loading insights…</div>;
  if (data.total_cases === 0)
    return (
      <div className="p-10 text-center border border-slate-200 rounded-md bg-white">
        <BarChart2 className="h-8 w-8 mx-auto text-slate-400" strokeWidth={1.5} />
        <div className="mt-3 font-medium text-slate-900">No cohort data yet</div>
        <div className="text-sm text-slate-500 mt-1">Insights appear once cases are saved and outcomes are recorded.</div>
      </div>
    );

  const shown = data.factors.filter((f) => f.count > 0);

  return (
    <div className="space-y-8" data-testid="insights-pane">
      {/* Headline stats */}
      <div className="grid sm:grid-cols-4 gap-4">
        <MiniStat label="Total cases" value={data.total_cases} testId="ins-total" />
        <MiniStat label="Outcomes tracked" value={data.tracked_cases} sub={`${data.total_cases - data.tracked_cases} pending`} testId="ins-tracked" />
        <MiniStat label="Success rate" value={`${data.success_rate}%`} sub={`${data.success_count} clinical / ongoing / live`} testId="ins-success-rate" />
        <MiniStat label="Distinct factors" value={shown.length} sub="Seen in ≥1 case" testId="ins-factors" />
      </div>

      {/* Factor frequency chart */}
      <div className="border border-slate-200 bg-white rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 className="h-4 w-4 text-blue-900" strokeWidth={1.75} />
          <div className="eyebrow">Factor frequency across cohort</div>
        </div>
        {shown.length === 0 ? (
          <div className="text-sm text-slate-500">No positive factors recorded yet.</div>
        ) : (
          <div style={{ width: "100%", height: Math.max(320, shown.length * 26) }} data-testid="insights-chart">
            <ResponsiveContainer>
              <BarChart data={shown} layout="vertical" margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} stroke="#64748B" tick={CHART_TICK_XS} />
                <YAxis type="category" dataKey="label" width={190} stroke="#334155" tick={CHART_TICK_YSLATE} />
                <Tooltip formatter={(v, _n, p) => [`${v} case${v === 1 ? "" : "s"} · ${p.payload.pct}%`, "Count"]} cursor={CHART_CURSOR} />
                <Bar dataKey="count" radius={BAR_RADIUS}>
                  {shown.map((entry) => (
                    <Cell key={entry.key} fill={entry.success_rate >= 50 ? "#0F766E" : "#1E3A8A"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-teal-700 inline-block" /> ≥50% success rate on this factor</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-blue-900 inline-block" /> Below 50% (or too few outcomes yet)</span>
        </div>
      </div>

      {/* Outcome distribution */}
      <div className="border border-slate-200 bg-white rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-4 w-4 text-blue-900" strokeWidth={1.75} />
          <div className="eyebrow">Outcome distribution</div>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="outcome-dist">
          {data.outcomes.map((o) => (
            <div key={o.outcome} className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <span className="text-sm text-slate-700">{outcomeLabel(o.outcome)}</span>
              <span className="font-mono text-slate-900 font-semibold">{o.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Success rate per factor table */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="p-5 pb-3 eyebrow">Success rate per factor</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-semibold text-slate-700">Factor</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-700">Cases with factor</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-700">Successes</th>
              <th className="text-right px-4 py-2 font-semibold text-slate-700">Success rate</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => (
              <tr key={f.key} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-700">{f.label}</td>
                <td className="px-4 py-2 text-right font-mono text-slate-900">{f.count} <span className="text-slate-400">({f.pct}%)</span></td>
                <td className="px-4 py-2 text-right font-mono text-slate-900">{f.success_count}</td>
                <td className={`px-4 py-2 text-right font-mono font-semibold ${f.success_rate >= 50 ? "text-teal-700" : "text-slate-900"}`}>{f.success_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, testId }) {
  return (
    <div className="border border-slate-200 bg-white p-4 rounded-md" data-testid={testId}>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 font-mono text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function ReferrersPane({ referrers }) {
  if (!referrers || referrers.length === 0) return null;
  return (
    <div className="mt-8 border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="referrers-pane">
      <div className="p-5 pb-3 flex items-center gap-2">
        <Stethoscope className="h-4 w-4 text-blue-900" strokeWidth={1.75} />
        <div className="eyebrow">Referring doctors / clinics</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-200">
          <tr>
            <th className="text-left px-4 py-2 font-semibold text-slate-700">Referrer</th>
            <th className="text-right px-4 py-2 font-semibold text-slate-700">Cases</th>
            <th className="text-right px-4 py-2 font-semibold text-slate-700">Tracked</th>
            <th className="text-right px-4 py-2 font-semibold text-slate-700">Avg factors</th>
            <th className="text-right px-4 py-2 font-semibold text-slate-700">Success rate</th>
          </tr>
        </thead>
        <tbody>
          {referrers.map((r) => (
            <tr key={r.referrer} className="border-t border-slate-100" data-testid={`referrer-row-${r.referrer.replace(/\W+/g, "-").toLowerCase()}`}>
              <td className="px-4 py-2 text-slate-700">{r.referrer}</td>
              <td className="px-4 py-2 text-right font-mono text-slate-900">{r.count}</td>
              <td className="px-4 py-2 text-right font-mono text-slate-500">{r.tracked_count}</td>
              <td className="px-4 py-2 text-right font-mono text-slate-900">{r.avg_factor_count}</td>
              <td className={`px-4 py-2 text-right font-mono font-semibold ${r.success_rate >= 50 ? "text-teal-700" : "text-slate-900"}`}>{r.tracked_count > 0 ? `${r.success_rate}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
